import React, { useState } from "react";
import { Button, Modal, Form, Input, Typography, notification } from "antd";
import { UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined, NumberOutlined, TagsOutlined, TrademarkCircleOutlined, CalendarOutlined, GlobalOutlined } from '@ant-design/icons';
import TextArea from "antd/es/input/TextArea";
import { api } from "../../services/api";
import { inputStyle, textareaStyle } from "./formStyles";
import { PRIMARY_COLOR } from "../../shared/colors";

const FormMarcaModal = ({ isOpen, setIsOpen }) => {
  const [form] = Form.useForm();
  const { Title, Text } = Typography;
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const emailValidator = (_, value) => {
    if (!value || value.includes("@")) {
      return Promise.resolve();
    }
    return Promise.reject("Ingrese un correo válido");
  };
  const numberValidator = (_, value) => {
    if (!value || /^[0-9]+$/.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject("Solo números");
  };

  const handleFieldsChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    const allTouched = form.getFieldsValue([
      "name", "rubro", "marca", "experiencia", "rrss", "phone", "mail", "locality", "code", "message"
    ]);
    const allFilled = Object.values(allTouched).every(val => !!val);
    setIsFormValid(!hasErrors && allFilled);
  };

  const handleCancel = () => {
    form.resetFields();
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (Object.values(values).every(value => value)) {
        setLoading(true);
        const response = await api.consultas.send({
          formType: "marca",
          nombre: values.name,
          rubro: values.rubro,
          marca: values.marca,
          experiencia: values.experiencia,
          sitioWeb: values.rrss,
          telefono: values.phone,
          email: values.mail,
          localidad: values.locality,
          codigoPostal: values.code,
          mensaje: values.message
        });
        if (response.status === "success") {
          notification.success({
            message: '¡Formulario enviado con éxito!',
            description: 'Gracias por contactarnos. Nos pondremos en contacto contigo pronto.',
            placement: 'topRight',
            duration: 4,
            style: { borderRadius: '8px' }
          });
          form.resetFields();
          setIsOpen(false);
        } else {
          throw new Error(response.message || "Error al enviar el formulario");
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error detallado',
        description: error.toString(),
        placement: 'top',
        duration: 6,
        style: { borderRadius: '8px', whiteSpace: 'pre-wrap' }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: 'center', margin: "20px 0" }}>
          <Title level={4} style={{ margin: 0, fontFamily: "Poppins", color: PRIMARY_COLOR, fontSize: "20px" }}>¿Querés sumar tu marca?</Title>
          <Text type="secondary" style={{ fontFamily: "Poppins" }}>
            ¡Completá este formulario y nos pondremos en contacto!
          </Text>
        </div>
      }
      className="custom-modal"
      open={isOpen}
      style={{ top: 20 }}
      onCancel={handleCancel}
      styles={{
        body: { maxHeight: 'calc(90vh - 200px)', overflowY: 'auto', paddingBottom: '24px' },
        mask: { backgroundColor: 'rgba(0, 0, 0, 0.45)' },
      }}
      footer={[
        <Button key="back" type="text" onClick={handleCancel} disabled={loading}>
          Cancelar
        </Button>,
        <Button key="submit" className="btn-option" loading={loading} onClick={handleSubmit} disabled={loading || !isFormValid}>
          Enviar consulta
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" onFieldsChange={handleFieldsChange}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0', padding: "15px" }}>
          <Form.Item name="name" label="Nombre y Apellido" rules={[{ required: true, message: "" }]} hasFeedback help={false}>
            <Input style={inputStyle} required variant="borderless" placeholder="Juan Perez" prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: "4px" }} />} />
          </Form.Item>
          <Form.Item name="rubro" label="Rubro / Categoría de producto" rules={[{ required: true, message: "" }]} hasFeedback help={false}>
            <Input style={inputStyle} variant="borderless" placeholder="" prefix={<TagsOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: "4px" }} />} />
          </Form.Item>
          <Form.Item name="marca" label="Marca" rules={[{ required: true, message: "" }]} hasFeedback help={false}>
            <Input style={inputStyle} variant="borderless" placeholder="" prefix={<TrademarkCircleOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: "4px" }} />} />
          </Form.Item>
          <Form.Item name="experiencia" label="Experiencia en el mercado (en años)" rules={[{ required: true, message: "" }, { validator: numberValidator }]} hasFeedback help={false}>
            <Input style={inputStyle} variant="borderless" placeholder="" prefix={<CalendarOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: "4px" }} />} inputMode="numeric" pattern="[0-9]*" />
          </Form.Item>
          <Form.Item name="rrss" label="Sitio web / Redes Sociales" rules={[{ required: true, message: "" }]} hasFeedback help={false}>
            <Input style={inputStyle} variant="borderless" placeholder="" prefix={<GlobalOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: "4px" }} />} />
          </Form.Item>
          <Form.Item name="phone" label="Teléfono" rules={[{ required: true, message: "" }, { validator: numberValidator }]} hasFeedback help={false}>
            <Input style={inputStyle} variant="borderless" placeholder="0800-777-6468" prefix={<PhoneOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: "4px" }} />} inputMode="numeric" pattern="[0-9]*" />
          </Form.Item>
          <Form.Item name="mail" label="Correo electrónico" rules={[{ required: true, message: "" }, { validator: emailValidator }]} hasFeedback help={false}>
            <Input style={inputStyle} variant="borderless" placeholder="" prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: "4px" }} />} />
          </Form.Item>
          <Form.Item name="locality" label="Localidad" rules={[{ required: true, message: "" }]} hasFeedback help={false}>
            <Input style={inputStyle} variant="borderless" placeholder="" prefix={<EnvironmentOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: "4px" }} />} />
          </Form.Item>
          <Form.Item name="code" label="Código Postal" rules={[{ required: true, message: "" }, { validator: numberValidator }]} hasFeedback help={false}>
            <Input style={inputStyle} variant="borderless" placeholder="" prefix={<NumberOutlined style={{ color: 'rgba(0,0,0,.25)', marginRight: "4px" }} />} inputMode="numeric" pattern="[0-9]*" />
          </Form.Item>
          <Form.Item name="message" label="Mensaje" rules={[{ required: true, message: "" }]} hasFeedback help={false}>
            <TextArea style={textareaStyle} variant="borderless" placeholder="Escribe tu mensaje aquí..." autoSize={{ minRows: 3, maxRows: 6 }} />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default FormMarcaModal;
